import { useState } from "react";
import { marketingConsultationSchema, type MarketingConsultationPayload } from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, SectionHeader } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function MarketingConsultation() {
  const [form, setForm] = useState<Partial<MarketingConsultationPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return <SuccessScreen message="Заявка пришла мне лично. Свяжусь и согласую время." />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = marketingConsultationSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("MARKETING_CONSULTATION", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Маркетинг — разовая консультация"
        title="90 минут Zoom со мной"
        subtitle="Плотный разбор твоего узкого места по фактам и цифрам. Без воды и теории."
        icon="📞"
        iconColor="blue"
      />
      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Не обучение и не «расскажи историю своей жизни».
            Плотные 90 минут — разбор конкретного запроса по фактам и цифрам.
          </Lead>
        </Card>

        <Card>
          <H3>На звонке мы:</H3>
          <CheckList items={[
            "Находим узкое место, из-за которого бизнес тормозит",
            "Собираем 3–5 конкретных гипотез на ближайшие 2 недели",
            "Расставляем приоритеты — что делать сейчас, что потом, что вообще не трогать",
          ]} />
        </Card>

        <Card>
          <H3>На выходе у тебя</H3>
          <CheckList items={[
            "Запись звонка — можешь пересматривать",
            "Конспект с гипотезами и приоритетами",
            "Список инструментов и материалов под внедрение",
          ]} />
        </Card>

        <Card>
          <H3>С чем приходят</H3>
          <P>
            Личный бренд и Instagram · Маркетинг-стратегия и каналы · Лидогенерация и воронки ·
            Запуск продукта или мероприятия · Системный маркетинг
          </P>
        </Card>

        <Card>
          <H3>Кому подойдёт</H3>
          <CheckList items={[
            "Уже есть продукт / бизнес / проект",
            "Есть конкретный запрос, а не «расскажи всё про маркетинг»",
            "Готовность внедрять, а не обсуждать",
          ]} />
        </Card>

        <div className="ios-callout-muted">
          Стоимость — по заявке. Скажу в ответном сообщении.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="@username или телефон" />
        </Field>
        <Field label="Ниша бизнеса / продукт" error={errors["niche"]}>
          <input className={`ios-input ${errors["niche"] ? "ios-input-error" : ""}`}
            value={form.niche ?? ""} onChange={(e) => setForm({ ...form, niche: e.target.value })} />
        </Field>
        <Field label="Месячный оборот / размер бизнеса" error={errors["monthlyRevenue"]}>
          <input className={`ios-input ${errors["monthlyRevenue"] ? "ios-input-error" : ""}`}
            value={form.monthlyRevenue ?? ""} onChange={(e) => setForm({ ...form, monthlyRevenue: e.target.value })} />
        </Field>
        <Field label="Что хочешь разобрать на консультации?" error={errors["request"]}>
          <textarea rows={4} className={`ios-input ${errors["request"] ? "ios-input-error" : ""}`}
            value={form.request ?? ""} onChange={(e) => setForm({ ...form, request: e.target.value })}
            placeholder="конкретный вопрос или боль" />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Отправить заявку</SubmitButton>
        </div>
      </form>
    </div>
  );
}
